import React from 'react';
import { coerceToDate } from '@common/paymentsUtils/dateUtils';

import { Box, Button, ButtonGroup, Card, Heading, Paragraph, Text, TextButton } from '@zillow/constellation-10';

import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import ONE_TIME_PAYMENT_TYPE from '@common/constants/oneTimePaymentTypes';
import TransactionConstants from '@common/constants/TransactionConstants';
import { printDaysBetweenDates } from '@common/paymentsUtils/dateUtils';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { capitalizeFirstLetter } from '@common/utils/stringUtils';
import TotalPaymentModule from '@renter-hub/components/TotalPaymentModule';
import { SelectedPaymentMethod } from '@renter-hub/components/SelectedPaymentMethod';
import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  today: Date;
  amount: number;
  dateRequested: string;
  dateCreated: string;
  notes: string;
  paymentType: string;
  paymentPlanInstallmentTitle?: string;
  otpKey: string;
  renterStatus: string;
  payOneTimePaymentOnClick: (otpKey: string) => void;
  goToEditPaymentInfoPage: () => void;
  zgFee: number;
  totalAmount: number;
  accountLast4?: string;
  accountName?: string;
  user: ZrmClickstreamUser;
}

interface State {
  expandOffline: boolean;
}
class DashboardOneTimePaymentCard extends React.Component<Props, State> {
  static defaultProps = {
    today: new Date(),
    notes: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      expandOffline: false,
    };
  }

  renderRequestedDate = () => {
    const { dateRequested, today } = this.props;
    const dateRequestedDate = coerceToDate(dateRequested) ?? new Date(dateRequested as any);
    return (
      <Paragraph textStyle="body-sm-bold">
        {capitalizeFirstLetter(printDaysBetweenDates(today, dateRequestedDate))}
      </Paragraph>
    );
  };

  renderAttemptedDate = () => {
    const { dateCreated, today } = this.props;
    const dateCreatedDate = coerceToDate(dateCreated) ?? new Date(dateCreated as any);
    return <Paragraph textStyle="body-sm-bold">Attempted {printDaysBetweenDates(today, dateCreatedDate)}</Paragraph>;
  };

  renderPaymentType = (paymentType) => {
    const { paymentPlanInstallmentTitle } = this.props;
    switch (paymentType) {
      case ONE_TIME_PAYMENT_TYPE.OTHER:
        return 'Other fee';
      case ONE_TIME_PAYMENT_TYPE.LATE_FEE:
        return 'Late fee';
      case ONE_TIME_PAYMENT_TYPE.PET_FEE:
        return 'Pet fee';
      case ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN:
        return paymentPlanInstallmentTitle;
      default:
        return capitalizeFirstLetter(paymentType);
    }
  };

  renderNotes = () =>
    this.props.notes && (
      <>
        <Paragraph textStyle="body-bold">Notes</Paragraph>
        {this.props.paymentType === 'other' && <Text>Other -&nbsp;</Text>}
        <Text>{this.props.notes}</Text>
      </>
    );

  renderTotalAmount = () => {
    const { amount } = this.props;
    return <TotalPaymentModule heading="Amount" totalAmount={amount} feeAmount={0} />;
  };

  renderPayButton = () => (
    <Button
      emphasis="filled"
      fluid={true}
      onClick={() => {
        RachelCSEvents.emitClicksStartOTPPaymentEvent({ user: this.props.user });
        this.props.payOneTimePaymentOnClick(this.props.otpKey);
      }}
    >
      Start payment
    </Button>
  );

  renderRetryButtons = () => (
    <ButtonGroup aria-label="retry button" orientation="vertical">
      <Button fluid={true} onClick={() => this.props.payOneTimePaymentOnClick(this.props.otpKey)}>
        Try payment again
      </Button>
      <Button fluid={true} onClick={this.props.goToEditPaymentInfoPage}>
        Change payment method
      </Button>
    </ButtonGroup>
  );

  renderPaidOfflineSection = () => (
    <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <TextButton textStyle="body-sm" onClick={() => this.setState({ expandOffline: !this.state.expandOffline })}>
        Did you pay this offline?
      </TextButton>
      {this.state.expandOffline && (
        <Paragraph className="ptm" css={{ color: 'text.subtle', textAlign: 'center' }} textStyle="body-sm">
          If you&apos;ve taken care of this payment, please contact your landlord to cancel it.
        </Paragraph>
      )}
    </Box>
  );

  renderUnpaidState = () => (
    <Box
      css={{
        display: 'grid',
        gridTemplateColumns: { base: '1fr', md: '7fr 5fr' },
        gap: '16px',
      }}
    >
      <Box>
        {this.renderRequestedDate()}
        <Heading level={2} textStyle="heading-xs" css={{ marginBottom: 'loose' }}>
          {this.renderPaymentType(this.props.paymentType)}
        </Heading>
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
          }}
        >
          <Box>{this.renderNotes()}</Box>
          <Box>{this.renderTotalAmount()}</Box>
        </Box>
      </Box>
      <Box>
        {this.renderPayButton()}
        {this.renderPaidOfflineSection()}
      </Box>
    </Box>
  );

  renderPaymentMethod = () => {
    const { accountName, accountLast4 } = this.props;
    if (accountName && accountLast4) {
      return <SelectedPaymentMethod useLabel={true} accountLast4={accountLast4} displayName={accountName} />;
    }
  };

  renderFailedState = () => {
    const { amount, paymentType } = this.props;
    const dollarAmount = centsToDollars(amount, { style: 'effectiveShort' });
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: { base: '1fr', md: '7fr 5fr' },
          gap: '16px',
        }}
      >
        <Box>
          {this.renderAttemptedDate()}
          <Text textStyle="body-lg">Your&nbsp;</Text>
          {paymentType !== ONE_TIME_PAYMENT_TYPE.OTHER && (
            <Text textStyle="body-lg-bold">{this.renderPaymentType(paymentType)}&nbsp;</Text>
          )}
          <Text textStyle="body-lg">payment for&nbsp;</Text>
          <Text textStyle="body-lg-bold">{dollarAmount}&nbsp;</Text>
          <Text textStyle="body-lg-bold" css={{ color: 'red.500' }}>
            failed.&nbsp;
          </Text>
          <Paragraph css={{ marginBottom: 'loose' }} textStyle="body-lg">
            Try a new payment method or contact your bank and try again.
          </Paragraph>
          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
            }}
          >
            <Box>{this.renderNotes()}</Box>
            <Box>{this.renderPaymentMethod()}</Box>
          </Box>
        </Box>
        <Box>
          {this.renderRetryButtons()}
          {this.renderPaidOfflineSection()}
        </Box>
      </Box>
    );
  };

  render() {
    const { renterStatus } = this.props;
    return (
      <Card css={{ padding: 'default', marginBottom: 'loose' }} elevated={false}>
        {renterStatus === TransactionConstants.STATUS.UNPAID && this.renderUnpaidState()}
        {renterStatus === TransactionConstants.STATUS.PAYMENT_FAILED && this.renderFailedState()}
      </Card>
    );
  }
}

export default DashboardOneTimePaymentCard;
